import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Table } from "@pentabd/ui";

import { OrderGroup, SearchBox, SelectControllerVertical } from "@components";
import { TableColumn } from "./TableColumn";

import { useSeniors } from "@hooks/HRM/employees/useSeniors";
import { useLanguage } from "@hooks/useLanguage";
import { useBaseData } from "@hooks/useBaseData";

import { BASE_DATA } from "@constants/base-data";

import { TablePagination } from "@helpers/tablePagination";
import {
  getConvertedPositionData,
  getConvertedUnitData,
  getConvertedOfficeAddress,
} from "@helpers/baseDataConvert";
import { downloadAcrReport } from "@api/acrReports";
import { useKeycloak } from "@react-keycloak/web";

const { DESIGNATIONS, WINGS, OFFICE } = BASE_DATA;

export const AcrReportsTable = () => {
  const [activeStatus, setActiveStatus] = useState<number | null>(null);

  const { t } = useTranslation();

  const { language } = useLanguage();
  const { keycloak } = useKeycloak();
  const token = keycloak.token as string;

  const { data: positions } = useBaseData(DESIGNATIONS);
  const { data: wings } = useBaseData(WINGS);
  const { data: office } = useBaseData(OFFICE);

  const {
    employees,
    pagination,
    employeeRequest,
    handleQuery,
    handleOrder,
    queryParams,
  } = useSeniors();
  const { total } = pagination || {};

  const { control, register, setValue } = useForm();

  const orderGroups = [
    {
      value: "gradeOrder",
      label: t("SENIORITY_LIST.ORDERS.EMPLOYEE_GRADE"),
    },
    {
      value: "joiningDateOrder",
      label: t("SENIORITY_LIST.ORDERS.JOINING_DATE"),
    },
    {
      value: "doBOrder",
      label: t("SENIORITY_LIST.ORDERS.DATE_OF_BIRTH"),
    },
    {
      value: "promotionOrder",
      label: t("SENIORITY_LIST.ORDERS.PROMOTION_DATE"),
    },
  ];

  const status = orderGroups.map((item) => item.value);

  const ordGroupAction = (index: number) => {
    let statusId = 0;
    switch (index) {
      case 0:
        statusId = 0;
        break;
      case 1:
        statusId = 1;
        break;
      case 2:
        statusId = 2;
        break;
      case 3:
        statusId = 3;
        break;
      default:
        statusId = 0;
        break;
    }

    setActiveStatus(statusId);
    handleOrder(status[statusId]);
  };

  const handleDownload = async (employeeIds: number[]) => {
    try {
      // Convert array to comma-separated string
      const employeeIdsString = employeeIds.join(",");

      await downloadAcrReport(token, t("REPORTS.ACR_REPORTS.HEADER"), {
        employeeIds: employeeIdsString, // Send as a string parameter
      });
    } catch (error) {
      console.error("Error downloading report:", error);
    }
  };

  return (
    <Table
      loading={employeeRequest}
      header={{
        title: t("SENIORITY_LIST.TABLE_HEADER.LABEL"),
        badge: {
          label: `${total || 0}`,
          size: "sm",
          type: "primary",
        },
        buttons: [
          <SearchBox
            key="search"
            control={control}
            handleQuery={handleQuery}
          />,
        ],
      }}
      columns={TableColumn(t)}
      rows={employees}
      check={{
        onClickDelete: (ids) => {
          handleDownload(ids as any); // Pass the IDs directly to handleDownload
        },
      }}
      selectButtonLabel={t("REPORTS.DOWNLOAD_BTN")}
      selectButtonProps={{
        size: "sm",
        type: "primary",
      }}
      headerExtension={{
        leftComponents: [
          <OrderGroup
            key="btn-group"
            orderGroupData={orderGroups}
            activeState={activeStatus}
            action={ordGroupAction}
            query={queryParams}
          />,
        ],
        rightComponents: [
          <div className="row promotion-candidate-list">
            <SelectControllerVertical
              control={control}
              label="PROMOTION_CANDIDATES.TABLE.FORM.OFFICE"
              name="officeId"
              options={getConvertedOfficeAddress(office, language)}
              register={register}
              setValue={setValue}
              isSearchable
              selectAction={(e) => {
                handleQuery("officeId", e);
              }}
              portal
              cols={4}
              withoutLabel
            />

            <SelectControllerVertical
              control={control}
              label="PROMOTION_CANDIDATES.TABLE.FORM.DESIGNATION"
              name="positionId"
              options={getConvertedPositionData(positions, language)}
              register={register}
              setValue={setValue}
              isSearchable
              selectAction={(e) => {
                handleQuery("positionId", e);
              }}
              portal
              cols={4}
              withoutLabel
            />

            <SelectControllerVertical
              control={control}
              label="PROMOTION_CANDIDATES.TABLE.FORM.UNIT"
              name="unitId"
              options={getConvertedUnitData(
                wings.filter((item: any) => item?.name !== "ACC"),
                language,
              )}
              register={register}
              setValue={setValue}
              isSearchable
              selectAction={(e) => {
                handleQuery("unitId", e);
              }}
              portal
              cols={4}
              withoutLabel
            />
          </div>,
        ],
      }}
      pagination={TablePagination(pagination, handleQuery)}
    />
  );
};
